import { useQuery } from "@tanstack/react-query";
import { financialService } from "../services/financialService";

/** Billed / paid / outstanding stat tiles plus a scrollable event history for
 * one student, backed by the same student-statement report used by
 * Financial → Statements. Shared by the Students directory view modal, the
 * Financial statements picker, and the bursar dashboard lookup so the
 * billed/paid math only lives in one place. */
export default function StudentFinancialSummary({
  studentId,
  eventListClassName = "mt-3 max-h-48 space-y-1 overflow-y-auto text-sm",
}: {
  studentId: number | null | undefined;
  eventListClassName?: string;
}) {
  const statement = useQuery({
    queryKey: ["student-financial-statement", studentId],
    queryFn: () => financialService.getStudentStatement(studentId!),
    enabled: !!studentId,
  });

  if (!studentId) return null;

  if (statement.isLoading) {
    return <p className="mt-2 text-sm text-gray-500">Loading financial details…</p>;
  }
  if (statement.isError) {
    return <p className="mt-2 text-sm text-red-600">Financial details could not be loaded.</p>;
  }
  if (!statement.data) return null;

  const discounts = statement.data.events
    .filter((event) => event.type === "discount")
    .reduce((sum, event) => sum + Number(event.credit), 0);
  // Every receipt, including money held as credit that has not yet been
  // applied to an invoice. Older statements without receipts fall back to
  // what was applied.
  const receipts = statement.data.receipts ?? [];
  const creditHeld = Number(statement.data.credit_balance ?? 0);
  const paid = receipts.length
    ? receipts.reduce((sum, receipt) => sum + Number(receipt.amount) - Number(receipt.refunded), 0)
    : statement.data.events
        .filter((event) => event.type === "payment")
        .reduce((sum, event) => sum + Number(event.credit), 0);

  return (
    <>
      <div className="mt-3 grid grid-cols-3 gap-3 text-center">
        <div className="rounded bg-gray-50 p-2">
          <div className="text-xs text-gray-500">Billed</div>
          <div className="text-sm font-semibold">
            UGX{" "}
            {statement.data.events
              .filter((event) => event.type === "charge")
              .reduce((sum, event) => sum + Number(event.debit), 0)
              .toLocaleString()}
          </div>
          {discounts > 0 && (
            <div className="text-xs text-gray-500">Less discounts UGX {discounts.toLocaleString()}</div>
          )}
        </div>
        <div className="rounded bg-gray-50 p-2">
          <div className="text-xs text-gray-500">Paid</div>
          <div className="text-sm font-semibold text-green-700">
            UGX {paid.toLocaleString()}
          </div>
        </div>
        <div className="rounded bg-gray-50 p-2">
          <div className="text-xs text-gray-500">Outstanding</div>
          <div
            className={`text-sm font-semibold ${Number(statement.data.outstanding_balance) > 0 ? "text-red-600" : "text-green-700"}`}
          >
            UGX {Number(statement.data.outstanding_balance).toLocaleString()}
          </div>
          {creditHeld > 0 && (
            <div className="text-xs text-gray-500">Credit held UGX {creditHeld.toLocaleString()}</div>
          )}
        </div>
      </div>
      <div className={eventListClassName}>
        {statement.data.events.map((event, index) => (
          <div key={index} className="flex justify-between border-b py-1">
            <span className="text-gray-600">
              {event.date} · {event.description}
            </span>
            <span>{Number(event.balance).toLocaleString()}</span>
          </div>
        ))}
        {!statement.data.events.length && (
          <p className="py-2 text-gray-500">No financial activity recorded yet.</p>
        )}
      </div>
      {receipts.length > 0 && (
        <>
          <div className="mt-4 text-xs font-semibold uppercase text-gray-500">Payment history</div>
          <div className={eventListClassName}>
            {receipts.map((receipt, index) => {
              const parts = [
                [receipt.applied, "applied to invoices"],
                [receipt.held_as_credit, "held as credit"],
                [receipt.paid_directly, "paid directly"],
                [receipt.refunded, "refunded"],
              ]
                .filter(([amount]) => Number(amount) > 0)
                .map(([amount, label]) => `UGX ${Number(amount).toLocaleString()} ${label}`);
              return (
                <div key={index} className="border-b py-1">
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      {receipt.date} · {receipt.receipt_number} · {receipt.method}
                    </span>
                    <span>UGX {Number(receipt.amount).toLocaleString()}</span>
                  </div>
                  {parts.length > 0 && <div className="text-xs text-gray-500">{parts.join(" · ")}</div>}
                </div>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}
